import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaCalendarAlt, FaXRay, FaCheckCircle, FaSpinner, FaUpload, FaTrashAlt, FaDownload, FaEye, FaFilePdf, FaFileImage, FaMoneyBillWave, FaTimes, FaLock } from "react-icons/fa";
import moment from "moment";
import { MdDateRange, MdAccessTime } from "react-icons/md";
import Select from "react-select";
import Swal from 'sweetalert2';

export default function PetRadiologyComponent({ appointmentId, petId, doctorId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petRadiologies, setPetRadiologies] = useState([]);
    const [radiologies, setRadiologies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingRadiology, setEditingRadiology] = useState(null);
    const [selectedRadiologies, setSelectedRadiologies] = useState([]);
    const [recomendation, setRecomendation] = useState("");
    const [radiologyDate, setRadiologyDate] = useState("");
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [openNestedAccordion, setOpenNestedAccordion] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [isRecommended, setIsRecommended] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [showFileModal, setShowFileModal] = useState(false);
    const [selectedFileUrl, setSelectedFileUrl] = useState("");
    const [uploadingFileId, setUploadingFileId] = useState(null);
    const [hasRadiologyForAppointment, setHasRadiologyForAppointment] = useState(false);

    // Get user role from localStorage
    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Delete permission only for Admin
    const canDelete = () => {
        return userRole === 'Admin';
    };

    // File delete permission only for Admin
    const canDeleteFile = () => {
        return userRole === 'Admin';
    };

    // Edit permission for radiology
    const canEditRadiology = () => {
        return hasPermission("Appointment Radiology edit");
    };

    // Complete modal state
    const [completeModal, setCompleteModal] = useState(false);
    const [selectedCompleteRadiology, setSelectedCompleteRadiology] = useState(null);
    const [completePrice, setCompletePrice] = useState("");
    const [completeStatus, setCompleteStatus] = useState("pending");

    // Helper function to format price
    const formatPrice = (price) => {
        if (price === null || price === undefined || price === "") return '';
        const num = parseFloat(price);
        if (isNaN(num)) return '';
        return num % 1 === 0 ? num.toString() : num.toFixed(2);
    };

    // Toast notification functions
    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showWarningToast = (message) => {
        Swal.fire({
            icon: 'warning',
            title: t('warning'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    // Get status badge color
    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'processing':
                return 'warning';
            case 'pending':
                return 'info';
            case 'missed':
                return 'danger';
            case 'cancelled':
                return 'secondary';
            default:
                return 'secondary';
        }
    };

    useEffect(() => {
        fetchPetRadiologies();
    }, [appointmentId, petId]);

    const fetchPetRadiologies = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-radiology/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const radiologiesForPet = response.data.petRadiology || [];
                setPetRadiologies(radiologiesForPet);
                setRadiologies(response.data.radiology || []);
                const hasRadiology = radiologiesForPet.some(radiology => String(radiology?.appointment_id) === String(appointmentId));
                setHasRadiologyForAppointment(hasRadiology);
                // Set current appointment accordion to open by default
                setOpenAccordion(String(appointmentId));
            }
        } catch (error) {
            console.error("Error fetching pet radiologies:", error);
            showErrorToast(t('failed_to_load_radiologies'));
            setPetRadiologies([]);
            setRadiologies([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    const handleStatusChange = async (radiologyId, currentStatus) => {
        if (!canEditRadiology()) {
            showWarningToast(t('you_dont_have_permission_to_edit'));
            return;
        }

        try {
            const AuthToken = localStorage.getItem("authToken");
            const newStatus = currentStatus === 'completed' ? 'processing' : 'completed';
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-radiology-update/${radiologyId}`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetRadiologies();
                showSuccessToast(t('Status changed successfully!'));
            }
        } catch (error) {
            console.error("Error changing status:", error);
            showErrorToast(t('failed_to_change_status'));
        }
    };

    const handleRadiologySelect = (selectedOptions) => {
        const radiologiesList = selectedOptions.map(opt => ({
            radiology_id: opt.value,
            price: opt.price !== undefined && opt.price !== null ? opt.price.toString() : ""
        }));
        setSelectedRadiologies(radiologiesList);
    };

    const handleRadiologyPriceChange = (radiologyId, newPrice) => {
        const updatedRadiologies = selectedRadiologies.map(rad =>
            rad.radiology_id === radiologyId
                ? { ...rad, price: newPrice }
                : rad
        );
        setSelectedRadiologies(updatedRadiologies);
    };

    const handleRemoveRadiology = (radiologyId) => {
        const updatedRadiologies = selectedRadiologies.filter(rad => rad.radiology_id !== radiologyId);
        setSelectedRadiologies(updatedRadiologies);
    };

    const handleFileUpload = async (radiologyId, file) => {
        if (!canEditRadiology()) {
            showWarningToast(t('you_dont_have_permission_to_upload_file'));
            return;
        }

        if (!file) {
            showWarningToast(t('please_select_file'));
            return;
        }

        setUploading(true);
        setUploadingFileId(radiologyId);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const formData = new FormData();
            formData.append('radiology_file', file);

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-radiology-upload-file/${radiologyId}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data) {
                showSuccessToast(t('file_uploaded_successfully'));
                fetchPetRadiologies();
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            showErrorToast(t('failed_to_upload_file'));
        } finally {
            setUploading(false);
            setUploadingFileId(null);
        }
    };

    const handleDeleteFile = async (radiologyId) => {
        if (!canDeleteFile()) {
            showWarningToast(t('only_admin_can_delete_files'));
            return;
        }

        Swal.fire({
            title: t('Are you sure?'),
            text: t("Delete this file?"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
            preConfirm: async () => {
                try {
                    const AuthToken = localStorage.getItem("authToken");
                    const response = await axios.post(
                        `${process.env.REACT_APP_API_URL}/pet-radiology-delete-file/${radiologyId}`,
                        {},
                        { headers: { Authorization: `Bearer ${AuthToken}` } }
                    );

                    if (response.data) {
                        fetchPetRadiologies();
                        showSuccessToast(t('file_deleted_successfully'));
                        return true;
                    }
                } catch (error) {
                    console.error("Error deleting file:", error);
                    showErrorToast(t('failed_to_delete_file'));
                    return false;
                }
            }
        });
    };

    const handleViewFile = (fileUrl) => {
        const fullUrl = `${process.env.REACT_APP_IMG_URL}/${fileUrl}`;
        setSelectedFileUrl(fullUrl);
        setShowFileModal(true);
    };

    const handleDownloadFile = (fileUrl, fileName) => {
        const fullUrl = `${process.env.REACT_APP_IMG_URL}/${fileUrl}`;
        window.open(fullUrl, '_blank');
    };

    const handleAddRadiology = () => {
        if (!canEditRadiology()) {
            showWarningToast(t('you_dont_have_permission_to_add'));
            return;
        }
        setEditingRadiology(null);
        setSelectedRadiologies([]);
        setRecomendation("");
        setRadiologyDate("");
        setGeneralNote("");
        setExistingNoteId(null);
        setIsRecommended(false);
        setSelectedFile(null);
        setShowModal(true);
    };

    const handleEditRadiology = (radiology) => {
        if (!canEditRadiology()) {
            showWarningToast(t('you_dont_have_permission_to_edit'));
            return;
        }
        setEditingRadiology(radiology);
        setSelectedRadiologies([{
            radiology_id: radiology.radiology_id,
            price: radiology.price !== null && radiology.price !== undefined ? radiology.price.toString() : ""
        }]);
        setRecomendation(radiology.recomendation || "");
        setRadiologyDate(radiology.radiology_date || "");

        setIsRecommended(!!(radiology.recomendation && radiology.recomendation.trim()));

        if (radiology.note && Array.isArray(radiology.note) && radiology.note.length > 0) {
            setGeneralNote(radiology.note[0].note || "");
            setExistingNoteId(radiology.note[0].id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        setSelectedFile(null);
        setShowModal(true);
    };

    const handleDeleteRadiology = async (radiologyId) => {
        if (!canDelete()) {
            showWarningToast(t('only_admin_can_delete'));
            return;
        }

        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-radiology-delete/${radiologyId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetRadiologies();
                showSuccessToast(t('Radiology deleted successfully!'));
            }
        } catch (error) {
            console.error("Error deleting radiology:", error);
            showErrorToast(t('failed_to_delete_radiology'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (radiologyId, radiologyName) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteRadiology(radiologyId);
            }
        });
    };

    const handleOpenComplete = (radiology) => {
        setSelectedCompleteRadiology(radiology);
        setCompletePrice(radiology.price !== null && radiology.price !== undefined ? radiology.price.toString() : "");
        setCompleteStatus(radiology.reminder_status || "pending");
        setCompleteModal(true);
    };

    const resetCompleteModal = () => {
        setCompleteModal(false);
        setSelectedCompleteRadiology(null);
        setCompletePrice("");
        setCompleteStatus("pending");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedCompleteRadiology) {
            if (completeStatus === "completed") {
                const priceNum = completePrice === "" ? 0 : parseFloat(completePrice);
                if (priceNum <= 0) {
                    showWarningToast(t('please_enter_price'));
                    return;
                }
            }
        }

        if (!selectedCompleteRadiology) {
            if (!isRecommended && selectedRadiologies.length === 0) {
                showWarningToast(t('please_select_at_least_one_radiology'));
                return;
            }
        }

        // Process prices: convert empty strings to 0
        const processedRadiologies = selectedRadiologies.map(rad => ({
            radiology_id: rad.radiology_id,
            price: rad.price === "" || rad.price === null || rad.price === undefined ? 0 : parseFloat(rad.price) || 0
        }));

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            let url = "";
            let payload = {};

            if (selectedCompleteRadiology) {
                url = `${process.env.REACT_APP_API_URL}/pet-radiology-update/${selectedCompleteRadiology.id}`;
                payload = {
                    appointment_id: appointmentId,
                    radiology_id: selectedCompleteRadiology.radiology_id,
                    price: completePrice === "" ? 0 : parseFloat(completePrice) || 0,
                    reminder_status: completeStatus,
                    is_repeat: true,
                };
            } else {
                url = editingRadiology
                    ? `${process.env.REACT_APP_API_URL}/pet-radiology-update/${editingRadiology.id}`
                    : `${process.env.REACT_APP_API_URL}/pet-radiology`;

                if (editingRadiology) {
                    payload = {
                        appointment_id: appointmentId,
                        radiology_id: !isRecommended ? processedRadiologies[0]?.radiology_id : null,
                        price: !isRecommended ? processedRadiologies[0]?.price : null,
                        recomendation: isRecommended ? (recomendation || null) : null,
                        radiology_date: radiologyDate || null,
                        note: generalNote || null,
                    };
                    if (existingNoteId) {
                        payload.note_id = existingNoteId;
                    }
                } else {
                    payload = {
                        appointment_id: appointmentId,
                        radiologies: isRecommended ? [] : processedRadiologies.map(rad => ({
                            radiology_id: rad.radiology_id,
                            price: rad.price
                        })),
                        recomendation: isRecommended ? (recomendation || null) : null,
                        radiology_date: radiologyDate || null,
                        note: generalNote || null,
                    };
                }
            }

            if (!selectedCompleteRadiology && generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            } else if (!selectedCompleteRadiology && existingNoteId) {
                payload.note = "";
                payload.note_id = existingNoteId;
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: {
                    Authorization: `Bearer ${AuthToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                setShowModal(false);
                if (selectedCompleteRadiology) {
                    resetCompleteModal();
                    showSuccessToast(t('Radiology completed successfully!'));
                } else {
                    showSuccessToast(editingRadiology ? t('Radiology updated successfully!') : t('Radiology added successfully!'));
                }
                fetchPetRadiologies();
            }
        } catch (error) {
            console.error("Error saving radiology:", error);
            showErrorToast(error.response?.data?.message || t('failed_to_save_radiology'));
        } finally {
            setSubmitting(false);
        }
    };

    const getRadiologyOptions = () => {
        const options = [];
        radiologies.forEach(parent => {
            options.push({
                value: null,
                label: parent.name,
                isDisabled: true,
                isParent: true
            });

            if (parent.child_radiologies && parent.child_radiologies.length > 0) {
                parent.child_radiologies.forEach(child => {
                    options.push({
                        value: child.id,
                        label: `   └ ${child.name} - ${formatPrice(child.price || 0)}`,
                        price: child.price
                    });
                });
            }
        });
        return options;
    };

    const customSelectStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.data.isDisabled ? '#f0f0f0' : state.isFocused ? '#e0e0e0' : 'white',
            color: state.data.isDisabled ? '#999' : '#333',
            fontStyle: state.data.isDisabled ? 'italic' : 'normal',
            fontWeight: state.data.isDisabled ? 'bold' : 'normal',
            cursor: state.data.isDisabled ? 'not-allowed' : 'pointer',
            padding: '10px',
        }),
        control: (provided) => ({
            ...provided,
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#86b7fe'
            }
        })
    };

    const isImageFile = (filename) => {
        return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(filename);
    };

    const getThumbnailUrl = (fileUrl) => {
        return `${process.env.REACT_APP_IMG_URL}/${fileUrl}`;
    };

    const groupRadiologiesByAppointment = () => {
        const grouped = {};
        petRadiologies.forEach(radiology => {
            const appointmentIdKey = radiology.appointment_id;
            if (!grouped[appointmentIdKey]) {
                grouped[appointmentIdKey] = {
                    appointment: radiology.appointment,
                    radiologies: []
                };
            }
            grouped[appointmentIdKey].radiologies.push(radiology);
        });

        const sortedEntries = Object.entries(grouped).sort((a, b) => {
            const aId = String(a[0]);
            const bId = String(b[0]);
            const currentId = String(appointmentId);

            if (aId === currentId) return -1;
            if (bId === currentId) return 1;
            return 0;
        });

        return Object.fromEntries(sortedEntries);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading radiologies')}</p>
            </div>
        );
    }

    const radiologyOptions = getRadiologyOptions();
    const selectedOptions = selectedRadiologies.map(rad => {
        let radName = 'Unknown';
        for (const parent of radiologies) {
            const child = parent.child_radiologies?.find(c => c.id === rad.radiology_id);
            if (child) {
                radName = child.name;
                break;
            }
        }
        return {
            value: rad.radiology_id,
            label: radName,
            price: rad.price
        };
    }).filter(opt => opt.value);

    const totalAmount = selectedRadiologies.reduce((sum, rad) => {
        const price = rad.price === "" || rad.price === null || rad.price === undefined ? 0 : parseFloat(rad.price) || 0;
        return sum + price;
    }, 0);

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('radiologies list')}</h6>
                {isAppointmentOpen && !hasRadiologyForAppointment &&
                    hasPermission("Appointment Radiology Create") && canEditRadiology() && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddRadiology}
                            className="d-flex align-items-center gap-2"
                        >
                            <FaPlus size={12} /> {t('add radiology')}
                        </Button>
                    )}
            </div>

            {!petRadiologies || petRadiologies.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaXRay size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no radiologies found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="pet-radiologies-accordion"
                >
                    {Object.entries(groupRadiologiesByAppointment()).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, radiologies: appointmentRadiologies } = appointmentData;
                        const isCurrentAppointment = String(appointmentIdKey) === String(appointmentId);

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>

                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentRadiologies.length} {t('radiology(s)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-0">
                                    <Accordion
                                        activeKey={openNestedAccordion[appointmentIdKey]}
                                        onSelect={(key) => setOpenNestedAccordion({
                                            ...openNestedAccordion,
                                            [appointmentIdKey]: key
                                        })}
                                        className="nested-radiologies-accordion pe-3 ps-3 pt-3"
                                    >
                                        {appointmentRadiologies.map((radiology) => {
                                            if (!radiology) return null;
                                            const dateTime = formatDateTime(radiology.radiology_date);
                                            const noteText = radiology.note && Array.isArray(radiology.note) && radiology.note.length > 0
                                                ? radiology.note[0].note
                                                : null;

                                            return (
                                                <Accordion.Item eventKey={String(radiology.id)} key={radiology.id} className="mb-3 border rounded">
                                                    <Accordion.Header className="bg-light">
                                                        <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3 flex-wrap gap-2">
                                                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3 flex-wrap">
                                                                <FaStethoscope className="text-primary" />
                                                                {!radiology.recomendation && (
                                                                    <Badge bg="success" className="px-3 py-2">
                                                                        {radiology.radiology?.name || 'N/A'}
                                                                    </Badge>
                                                                )}
                                                                <div className="text-muted small">
                                                                    👨‍⚕️ <strong>Doctor:</strong> {radiology?.doctor?.name || 'Unknown Doctor'}
                                                                </div>
                                                                {radiology.recomendation && (
                                                                    <Badge bg="warning" className="px-3 py-2">
                                                                        {t('Recommended')}
                                                                    </Badge>
                                                                )}
                                                                {dateTime && !radiology.recomendation && (
                                                                    <div className="d-flex align-items-center gap-3 text-muted small">
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdDateRange size={14} />
                                                                            {moment(dateTime.date).format("DD MMM YYYY")}
                                                                        </span>
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdAccessTime size={14} />
                                                                            {moment(dateTime.time, "HH:mm:ss").format("hh:mm A")}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {/* Status Badge */}
                                                                {!radiology.recomendation && radiology.status && (
                                                                    <Badge bg={getStatusBadge(radiology.status)} className="px-3 py-2">
                                                                        <FaSpinner className="me-1" size={10} />
                                                                        {radiology.status}
                                                                    </Badge>
                                                                )}

                                                            </div>
                                                            <div className="d-flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                                                                {canEditRadiology() && (
                                                                    <>
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            onClick={() => handleEditRadiology(radiology)}
                                                                            className="d-flex align-items-center gap-1"
                                                                            style={{ background: '#3182ce', color: '#fff' }}
                                                                        >
                                                                            <FaEdit size={12} />
                                                                            <span className="d-none d-sm-inline">{t('edit')}</span>
                                                                        </Button>
                                                                    </>
                                                                )}
                                                                {canDelete() && (
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteClick(radiology.id, radiology.radiology?.name || 'Radiology')}
                                                                        className="d-flex align-items-center gap-1"
                                                                    >
                                                                        <FaTrash size={12} />
                                                                        <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body className="bg-white">
                                                        <Row className="g-4">
                                                            <Col md={12}>

                                                                <div className="p-3 bg-light rounded">
                                                                    {!radiology.recomendation ? (
                                                                        <>
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Radiology Name')}:</strong> {radiology.radiology?.name || 'N/A'}
                                                                            </div>
                                                                            {radiology.radiology?.description && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Description')}:</strong> {radiology.radiology?.description}
                                                                                </div>
                                                                            )}
                                                                            {radiology.radiology_date && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Radiology Date')}:</strong>{' '}
                                                                                    {moment(radiology.radiology_date).format("DD MMM YYYY hh:mm A")}
                                                                                </div>
                                                                            )}

                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Status')}:</strong>{' '}
                                                                                <Badge
                                                                                    bg={radiology.status === 'completed' ? 'success' : 'warning'}
                                                                                    className="ms-2"
                                                                                    style={{ cursor: canEditRadiology() ? 'pointer' : 'default' }}
                                                                                    onClick={() => {
                                                                                        if (canEditRadiology()) {
                                                                                            handleStatusChange(radiology.id, radiology.status);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {radiology.status === 'completed' ? t('completed') : t('processing')}
                                                                                </Badge>
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {radiology.recomendation && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Recommendation')}:</strong> {radiology.recomendation}
                                                                                </div>
                                                                            )}
                                                                            {radiology.radiology_date && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Radiology Date')}:</strong>{' '}
                                                                                    {moment(radiology.radiology_date).format("DD MMM YYYY hh:mm A")}
                                                                                </div>
                                                                            )}

                                                                        </>
                                                                    )}

                                                                    {/* File Upload Section */}
                                                                    <div className="mt-3 pt-2 border-top">
                                                                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FaUpload className="text-info" />
                                                                                <strong className="text-muted">{t('Radiology File')}:</strong>
                                                                            </div>
                                                                            {radiology.radiology_file && (
                                                                                <div className="d-flex gap-2 flex-wrap">
                                                                                    <Button
                                                                                        variant="info"
                                                                                        size="sm"
                                                                                        onClick={() => handleViewFile(radiology.radiology_file)}
                                                                                    >
                                                                                        <FaEye size={12} /> {t('view')}
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="success"
                                                                                        size="sm"
                                                                                        onClick={() => handleDownloadFile(radiology.radiology_file, radiology.radiology?.name || 'radiology')}
                                                                                    >
                                                                                        <FaDownload size={12} /> {t('download')}
                                                                                    </Button>
                                                                                    {canDeleteFile() && (
                                                                                        <Button
                                                                                            variant="danger"
                                                                                            size="sm"
                                                                                            onClick={() => handleDeleteFile(radiology.id)}
                                                                                        >
                                                                                            <FaTrashAlt size={12} /> {t('delete')}
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {radiology.radiology_file && (
                                                                            <div
                                                                                className="mb-2 file-thumbnail"
                                                                                style={{ cursor: 'pointer' }}
                                                                                onClick={() => handleViewFile(radiology.radiology_file)}
                                                                            >
                                                                                {isImageFile(radiology.radiology_file) ? (
                                                                                    <img
                                                                                        src={getThumbnailUrl(radiology.radiology_file)}
                                                                                        alt={radiology.radiology_file.split('/').pop()}
                                                                                        style={{
                                                                                            maxWidth: '100%',
                                                                                            maxHeight: '150px',
                                                                                            objectFit: 'contain',
                                                                                            border: '1px solid #ddd',
                                                                                            borderRadius: '4px',
                                                                                            padding: '5px'
                                                                                        }}
                                                                                        onError={(e) => {
                                                                                            e.target.style.display = 'none';
                                                                                            e.target.nextSibling.style.display = 'flex';
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <div
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: '10px',
                                                                                            padding: '10px',
                                                                                            border: '1px solid #ddd',
                                                                                            borderRadius: '4px',
                                                                                            backgroundColor: '#f8f9fa'
                                                                                        }}
                                                                                    >
                                                                                        {radiology.radiology_file.toLowerCase().includes('.pdf') ? (
                                                                                            <FaFilePdf size={40} color="red" />
                                                                                        ) : (
                                                                                            <FaFileImage size={40} color="#6c757d" />
                                                                                        )}
                                                                                        <span className="text-primary small">
                                                                                            {radiology.radiology_file.split('/').pop()}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {!radiology.radiology_file && canEditRadiology() && (
                                                                            <div className="mt-2">
                                                                                <Form.Group>
                                                                                    <Form.Control
                                                                                        type="file"
                                                                                        size="sm"
                                                                                        onChange={(e) => {
                                                                                            const file = e.target.files[0];
                                                                                            if (file) {
                                                                                                handleFileUpload(radiology.id, file);
                                                                                            }
                                                                                        }}
                                                                                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                                                                                        disabled={uploading && uploadingFileId === radiology.id}
                                                                                    />
                                                                                    {uploading && uploadingFileId === radiology.id && (
                                                                                        <Spinner animation="border" size="sm" className="mt-2" />
                                                                                    )}
                                                                                </Form.Group>
                                                                            </div>
                                                                        )}
                                                                        {!radiology.radiology_file && !canEditRadiology() && (
                                                                            <div className="mt-2 text-muted">
                                                                                <FaLock className="me-1" /> {t('No file uploaded')}
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {noteText && (
                                                                        <div className="mt-3 pt-2 border-top">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FaFileAlt className="text-success" />
                                                                                <strong className="text-muted">{t('note')}:</strong>
                                                                            </div>
                                                                            <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => {
                setShowModal(false);
                setSelectedCompleteRadiology(null);
            }} size="lg" centered>
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        {editingRadiology ? t('Edit Radiology') : t('Add Radiologies')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="custom-modal-body">
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="recommended-checkbox"
                                label={<span className="fw-bold">{t('Recommended')}</span>}
                                checked={isRecommended}
                                onChange={(e) => setIsRecommended(e.target.checked)}
                            />
                        </Form.Group>

                        {!isRecommended ? (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t('Select Radiologies')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                        options={radiologyOptions}
                                        value={selectedOptions}
                                        onChange={handleRadiologySelect}
                                        placeholder={t('select radiologies')}
                                        isMulti
                                        isSearchable
                                        styles={customSelectStyles}
                                        formatOptionLabel={(option) => (
                                            <span style={{
                                                marginLeft: option.isParent ? '0' : '20px',
                                                fontWeight: option.isParent ? 'bold' : 'normal',
                                                color: option.isParent ? '#666' : '#333'
                                            }}>
                                                {option.label}
                                            </span>
                                        )}
                                    />
                                    <small className="text-muted d-block mt-1">
                                        {t('You can select multiple radiologies')}
                                    </small>
                                </Form.Group>

                                {selectedRadiologies.length > 0 && (
                                    <div className="mb-3">
                                        <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                            <FaMoneyBillWave className="text-success" /> {t('Prices')}
                                        </Form.Label>
                                        {selectedRadiologies.map((rad, index) => {
                                            let radInfo = null;
                                            for (const parent of radiologies) {
                                                const child = parent.child_radiologies?.find(c => c.id === rad.radiology_id);
                                                if (child) {
                                                    radInfo = child;
                                                    break;
                                                }
                                            }
                                            return (
                                                <div key={index} className="mb-2 p-2 border rounded bg-light">
                                                    <Row className="align-items-center g-2">
                                                        <Col xs={12} sm={6}>
                                                            <strong>{radInfo?.name || 'Unknown Radiology'}</strong>
                                                        </Col>
                                                        <Col xs={8} sm={4}>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.01"
                                                                onFocus={(e) => e.target.select()}
                                                                value={rad.price ?? ''}
                                                                onChange={(e) => handleRadiologyPriceChange(rad.radiology_id, e.target.value)}
                                                                placeholder={t('Enter price')}
                                                            />
                                                        </Col>
                                                        <Col xs={2} sm={1} className="text-end">
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                onClick={() => handleRemoveRadiology(rad.radiology_id)}
                                                                className="d-flex align-items-center justify-content-center w-100"
                                                            >
                                                                <FaTimes />
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                    <div className="mt-1">
                                                        <small className="text-muted">
                                                            {t('Default price')}: {formatPrice(radInfo?.price || 0)}
                                                        </small>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <div className="mt-3 p-2 rounded text-end">
                                            <strong>{t('Total Amount')}: </strong>
                                            <span style={{ fontSize: '18px', fontWeight: 'bold', color: 'black' }}>
                                                {formatPrice(totalAmount)}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                        <FaCalendarAlt /> {t('Radiology Date')}
                                    </Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={radiologyDate}
                                        onChange={(e) => setRadiologyDate(e.target.value)}
                                    />
                                </Form.Group>
                            </>
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t('Recommendations')}
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={recomendation}
                                        onChange={(e) => setRecomendation(e.target.value)}
                                        placeholder={t('Enter recommendations here...')}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                        <FaCalendarAlt /> {t('Radiology Date')}
                                    </Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={radiologyDate}
                                        onChange={(e) => setRadiologyDate(e.target.value)}
                                    />
                                </Form.Group>
                            </>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaFileAlt /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t('cancel')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingRadiology ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>


            {/* File View Modal */}
            <Modal show={showFileModal} onHide={() => setShowFileModal(false)} size="xl" centered>
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title>{t('View Radiology File')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center" style={{ maxHeight: '80vh', overflow: 'auto' }}>
                    {selectedFileUrl && (
                        selectedFileUrl.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                            <img
                                src={selectedFileUrl}
                                alt="Radiology"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'contain'
                                }}
                            />
                        ) : selectedFileUrl.toLowerCase().includes('.pdf') ? (
                            <iframe
                                src={selectedFileUrl}
                                title="Radiology File"
                                style={{ width: '100%', height: '70vh' }}
                                className="border-0"
                            />
                        ) : (
                            <div className="text-center">
                                <FaFileImage size={80} className="text-muted mb-3" />
                                <p>{t('File preview not available')}</p>
                                <Button
                                    variant="primary"
                                    onClick={() => window.open(selectedFileUrl, '_blank')}
                                >
                                    <FaDownload /> {t('Open File')}
                                </Button>
                            </div>
                        )
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowFileModal(false)}>
                        {t('close')}
                    </Button>
                    <Button variant="primary" onClick={() => window.open(selectedFileUrl, '_blank')}>
                        <FaDownload /> {t('download')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}